import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { T, FONT_MONO } from '../constants/tokens';

const useStyles = makeStyles({
  card: {
    background: T.elevated,
    border: '1px solid ' + T.hairline,
    borderRadius: 6,
    padding: 24,
    minWidth: 0,
    textAlign: 'left',
    fontFamily: 'inherit',
    display: 'block',
    width: '100%'
  },
  clickable: {
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: T.surfaceSoft,
      borderColor: T.muted
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.focus,
      outlineOffset: 2
    }
  }
});

/**
 * A readiness tile: what it measures, the measure itself, and a line saying
 * what that means.
 *
 * The design leads with a ratio rather than a bare count — "18 / 19 hubs" says
 * considerably more at a glance than "1 offline", because it carries the scale
 * you are reading the number against. `detail` carries the consequence.
 */
export default function StatCard({ label, value, detail = null, accent = null, onClick = null, footer = null, style = {} }) {
  const classes = useStyles();

  // A card with its own buttons inside (the health bar's chips) cannot itself
  // be a button, so it becomes a focusable element that behaves like one.
  const nested = onClick && footer != null;
  const Tag = onClick && !nested ? 'button' : 'div';
  const asButton = nested
    ? {
      role: 'button',
      tabIndex: 0,
      onKeyDown: (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }
    }
    : {};

  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      onClick={onClick ?? undefined}
      className={[classes.card, onClick ? classes.clickable : ''].filter(Boolean).join(' ')}
      style={style}
      {...asButton}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          textTransform: 'uppercase',
          color: T.body
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 600,
          lineHeight: '32px',
          letterSpacing: '-0.72px',
          color: accent ?? T.ink,
          margin: '8px 0 4px',
          overflowWrap: 'anywhere'
        }}
      >
        {value}
      </div>
      {detail &&
        <div style={{ fontSize: 14, lineHeight: '20px', color: T.body, textWrap: 'pretty' }}>
          {detail}
        </div>}
      {footer}
    </Tag>
  );
}

StatCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  detail: PropTypes.node,
  accent: PropTypes.string,
  onClick: PropTypes.func,
  // Below the detail line, e.g. a DeviceHealthBar.
  footer: PropTypes.node,
  style: PropTypes.object
};
